import Styled, { css, SimpleInterpolation } from 'styled-components';

import { cssDeclaration, VALID_DIMENSION_DATA } from '../Utils';
import { media } from '../../../lib/StyledComponents/StyledComponents';
import { SVGIconStyleWrapper } from '../../Icon/SVGIcon';

export interface ITableProps {
  hasBorderSpacing?: boolean;
}

export interface IStyledTableTdProps {
  orderProp?: string;
  textAlign?: string;
}

export interface IStyledTableThProps extends IStyledTableTdProps {
  widthProp?: VALID_DIMENSION_DATA;
}

type VALID_SCOPE_ATTRIBUTES = 'col' | 'row' | 'colgroup' | 'rowgroup';

const defaultTableBorderStyles = css`
  content: '';
  width: 100%;
  position: absolute;
  top: 100%;

  ${({theme: { themeTwo }}) => css`
    z-index: ${themeTwo.global.zIndex.middle};
  `}
`;

export const Table = Styled.table<ITableProps>`
  width: 100%;

  ${({theme: { themeTwo }, hasBorderSpacing }) => {
    const { table } = themeTwo;

    return css`
      /* background-color */ ${cssDeclaration(themeTwo, 'background-color', table.backgroundColor)};
      /* border */ ${cssDeclaration(themeTwo, 'border', table.border)};
      /* padding */ ${cssDeclaration(themeTwo, 'padding', table.padding)};
      text-indent: ${table.font.textIndent};

      ${hasBorderSpacing && css`
        border-collapse: separate;
        border-spacing: ${table.borderSpacing || '3.4rem 0'};
      `}
    `;
  }}
`;

export const TableTr = Styled.tr<{scope: VALID_SCOPE_ATTRIBUTES}>`
  width: 100%;
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: space-between;
  position: relative;

  /* mobile border bottom rows */
  &:after {
    ${defaultTableBorderStyles}

    ${({theme: { themeTwo }}) => {
      const { table } = themeTwo;
      const borderGutter = themeTwo.global.edgeSize[table.tableRow.borderGutter] || table.tableRow.borderGutter;

      return css`
        width: calc(100% - calc(${table.tableRow.borderGutter} * 2));
        left: ${borderGutter || '0px'};
        height: ${table.tableRow.height || '1px'};

        ${media.mediumLarge`
          content: none;
        `}
      `;
    }}
  }

  ${media.mediumLarge`
    display: table-row;
    position: relative;
  `}
`;

export const StyledTableTh = Styled.th<IStyledTableThProps>`
  display: none;

  &:first-of-type,
  &:last-of-type {
    display: table-cell;
  }

  ${({theme: { themeTwo }, textAlign, orderProp, widthProp }) => {
    const { table } = themeTwo;
    const borderGutter = themeTwo.global.edgeSize[table.tableRow.borderGutter] || table.tableRow.borderGutter;

    return css`
      font-weight: ${table.tableHeader.font.weight};
      text-transform: ${table.tableHeader.font.textTransform};

      /* font-size */ ${cssDeclaration(themeTwo, 'font-size', table.tableHeader.font.size)};
      /* color */ ${cssDeclaration(themeTwo, 'color',  table.tableHeader.font.color)};
      /* padding */ ${cssDeclaration(themeTwo, 'padding', table.tableHeader.padding)};
      /* width */ ${widthProp && cssDeclaration(themeTwo, 'width', widthProp)};
      /* text-align */ ${textAlign && cssDeclaration(themeTwo, 'text-align', textAlign)};
      /* order */ ${orderProp && cssDeclaration(themeTwo, 'order', orderProp)};

      &:after {
        /* th desktop divider - background-color */ ${cssDeclaration(themeTwo, 'background-color', table.tableHeader.rowDivider)};
      }

      ${media.mediumLarge`
        display: table-cell;
        position: relative;

        &:after {
          ${defaultTableBorderStyles as SimpleInterpolation}
          left: ${borderGutter || '0px'};
          height: ${table.tableRow.height || '1px'};
        }

        &:last-of-type:after {
          left: -${borderGutter};
        }

        &:only-of-type {
          &:after {
            width: calc(100% - calc(${table.tableRow.borderGutter || '0px'} * 2));
            left: ${borderGutter || '0px'};
            height: ${table.tableRow.height || '1px'};
          }
        }
      `}
    `;
  }}
`;

export const StyledTableTd = Styled.td<IStyledTableTdProps>`
  display: inline-flex;
  width: 50%;
  &:nth-of-type(even) {
    justify-content: flex-end;
  }

  ${SVGIconStyleWrapper} {
    display: inline-block;
  }

  ${({theme: { themeTwo }, orderProp, textAlign }) => {
    const { table } = themeTwo;
    const borderGutter = themeTwo.global.edgeSize[table.tableRow.borderGutter] || table.tableRow.borderGutter;

    return css`
      font-weight: ${table.tableBody.font.weight};
      /* padding */ ${cssDeclaration(themeTwo, 'padding', table.tableBody.padding)};
      /* font-size */ ${cssDeclaration(themeTwo, 'font-size', table.tableBody.font.size)};
      /* color */ ${cssDeclaration(themeTwo, 'color', table.tableBody.font.color)};
      /* text-align */ ${textAlign && cssDeclaration(themeTwo, 'text-align', textAlign)};
      /* order */ ${orderProp && cssDeclaration(themeTwo, 'order', orderProp)};

      &:after {
        /* background-color */ ${cssDeclaration(themeTwo, 'background-color', table.tableBody.rowDivider)};
      }

      ${media.mediumLarge`
        display: table-cell;
        position: relative;
        width: revert;

        &:after {
          ${defaultTableBorderStyles as SimpleInterpolation}
        }

        /* colSpan Fix */
        &:not(:only-of-type) {
          &:after {
            left: ${borderGutter || '0px'};
            height: ${table.tableRow.height || '1px'};
          }

          &:last-of-type:after {
            left: -${borderGutter};
          }
        }

        &:only-of-type {
          &:after {
            width: calc(100% - calc(${table.tableRow.borderGutter || '0px'} * 2));
            left: ${borderGutter || '0px'};
            height: ${table.tableRow.height || '1px'};
          }
        }
      `}
    `;
  }}
`;

export const SubHeader = Styled(TableTr)<{scope: VALID_SCOPE_ATTRIBUTES}>`
  ${({theme: { themeTwo }}) => {
    const { table } = themeTwo;

    return css`
      display: inline-table;
      /* background-color */ ${cssDeclaration(themeTwo, 'background-color', table.subHeader.backgroundColor || 'info')};

      &:after {
        content: none;
      }

      ${media.mediumLarge`
        display: table-row;
      `}

      ${StyledTableTh} {
        background-color: transparent;
        font-weight: ${table.subHeader.font.weight};
        /* border */ ${cssDeclaration(themeTwo, 'border', table.subHeader.border || 'none')};
        /* color */ ${cssDeclaration(themeTwo, 'color',  table.subHeader.font.color)};
        /* font-size */ ${cssDeclaration(themeTwo, 'font-size', table.subHeader.font.size)};
      }
    `;
  }}
`;

export const TableHeader = Styled.thead`
  ${({ theme: { themeTwo }}) => {
    const { table } = themeTwo;

    return css`
      line-height: ${table.tableHeader.font.lineHeight};
      /* background-color */ ${cssDeclaration(themeTwo, 'background-color', table.tableHeader.backgroundColor)}

      ${TableTr}:after {
        /* header mobile divider - background-color */ ${cssDeclaration(themeTwo, 'background-color', table.tableHeader.rowDivider)};
      }
    `;
  }}
`;

export const TableBody = Styled.tbody`
  ${({theme: { themeTwo }}) => {
    const { table: { tableBody }} = themeTwo;

    return css`
      line-height: ${tableBody.font.lineHeight};
      /* background-color */ ${cssDeclaration(themeTwo, 'background-color', tableBody.backgroundColor)}
      /* box-shadow */ ${cssDeclaration(themeTwo, 'box-shadow', tableBody.boxShadow)};

      ${TableTr}:after {
        /* body mobile divider - background-color */ ${cssDeclaration(themeTwo, 'background-color', tableBody.rowDivider)};

        ${media.mediumLarge`
          content: none;
        `}
      }

      /* Table Body Border Radius */
      ${TableTr}:first-of-type {
        ${StyledTableTd}:first-of-type {
          /* border-radius */ ${tableBody.borderRadius.topLeft &&
            cssDeclaration(themeTwo, 'border-top-left-radius', tableBody.borderRadius.topLeft)};
        }
        ${StyledTableTd}:last-of-type {
          /* border-radius */ ${tableBody.borderRadius.topRight &&
            cssDeclaration(themeTwo, 'border-top-right-radius', tableBody.borderRadius.topRight)};
        }
      }
      ${TableTr}:last-of-type {
        ${StyledTableTd}:first-of-type {
          /* border-radius */ ${tableBody.borderRadius.bottomLeft &&
            cssDeclaration(themeTwo, 'border-bottom-left-radius', tableBody.borderRadius.bottomLeft)};
        }
        ${StyledTableTd}:last-of-type {
          /* border-radius */ ${tableBody.borderRadius.bottomRight &&
            cssDeclaration(themeTwo, 'border-bottom-right-radius', tableBody.borderRadius.bottomRight)};
        }
      }
    `;
    }
  };

  ${TableTr}:last-of-type {
    ${StyledTableTd}:after {
      background: none;
    }

    ${StyledTableTh}:after {
      background: none;
    }

    &:after {
      background: none;
    }
  }
`;
